import { useMemo } from 'react';

import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import FormFieldId from '../../../types/FormFieldId';
import FormFieldType from '../../../types/FormFieldType';
import { labels } from '../../../util/formLabels';
import { normalizeBooleanField } from '../../../util/checkoutFormUtils';
import {
  isEmail,
  maxLength,
  minLength,
  notBlank,
} from '../../../util/formValidators';
import safeCloneDeep from '../../../util/safeCloneDeep';

const b2cBillingFormLabels = {
  [FormFieldId.BILLING_COMPANY]: labels.company,
  [FormFieldId.BILLING_VAT_NUMBER]: labels.businessId,
  [FormFieldId.BILLING_FIRST_NAME]: labels.firstName,
  [FormFieldId.BILLING_LAST_NAME]: labels.lastName,
  [FormFieldId.BILLING_STREET_ADDRESS]: labels.streetAddress,
  [FormFieldId.BILLING_POSTAL_CODE]: labels.postalCode,
  [FormFieldId.BILLING_CITY]: labels.city,
  [FormFieldId.BILLING_EMAIL]: labels.email,
  [FormFieldId.BILLING_PHONE]: labels.telephone,
};

const b2cDeliveryFormLabels = {
  [FormFieldId.DELIVERY_COMPANY]: labels.deliveryCompany,
  [FormFieldId.DELIVERY_FIRST_NAME]: labels.firstName,
  [FormFieldId.DELIVERY_LAST_NAME]: labels.lastName,
  [FormFieldId.DELIVERY_STREET_ADDRESS]: labels.deliveryStreetAddress,
  [FormFieldId.DELIVERY_POSTAL_CODE]: labels.deliveryPostalCode,
  [FormFieldId.DELIVERY_CITY]: labels.deliveryCity,
  [FormFieldId.DELIVERY_COUNTRY]: labels.country,
  [FormFieldId.DELIVERY_PHONE]: labels.deliveryTelephone,
  [FormFieldId.DELIVERY_ADDRESS_DROPDOWN]: labels.deliveryAddressId,
};

/**
 * Order country can be numeric id, string id, or { id, name } from the API.
 * CheckoutDeliveryForm uses <option value={String(o.value)} /> — the MobX form
 * value must use the same string or the controlled select and floating label desync.
 */
function snapshotDeliveryCountrySelectForForm(formField) {
  const snapshot = safeCloneDeep(formField);
  if (
    formField.id !== FormFieldId.DELIVERY_COUNTRY ||
    formField.type !== FormFieldType.SELECT
  ) {
    return snapshot;
  }
  const raw = formField.value;
  let normalized = '';
  if (raw !== null && raw !== undefined && raw !== '') {
    if (typeof raw === 'object' && raw != null && raw.id != null) {
      normalized = String(raw.id);
    } else {
      normalized = String(raw);
    }
  }
  return { ...snapshot, value: normalized };
}

const b2bDeliveryFormLabels = {
  [FormFieldId.DELIVERY_COMPANY]: labels.deliveryCompany,
  [FormFieldId.DELIVERY_FIRST_NAME]: labels.firstName,
  [FormFieldId.DELIVERY_LAST_NAME]: labels.lastName,
  [FormFieldId.DELIVERY_STREET_ADDRESS]: labels.deliveryStreetAddress,
  [FormFieldId.DELIVERY_POSTAL_CODE]: labels.deliveryPostalCode,
  [FormFieldId.DELIVERY_CITY]: labels.deliveryCity,
  [FormFieldId.DELIVERY_COUNTRY]: labels.country,
  [FormFieldId.DELIVERY_PHONE]: labels.deliveryTelephone,
  [FormFieldId.DELIVERY_CARE_OF]: labels.deliveryCareOf,
};

const getCheckoutFieldValidators = (formField, formLabels) => {
  const validators = [];
  if (formField.required) {
    validators.push(notBlank(formLabels[formField.id]));
  }
  if (formField.min_length) {
    validators.push(minLength(formLabels[formField.id], formField.min_length));
  }
  if (formField.max_length) {
    validators.push(maxLength(formLabels[formField.id], formField.max_length));
  }
  if (formField.type === FormFieldType.EMAIL) {
    validators.push(isEmail(labels.email));
  }
  return validators;
};

const b2bBillingFormLabels = {
  [FormFieldId.BILLING_COMPANY]: labels.company,
  [FormFieldId.BILLING_VAT_NUMBER]: labels.businessId,
  [FormFieldId.BILLING_FIRST_NAME]: labels.firstName,
  [FormFieldId.BILLING_LAST_NAME]: labels.lastName,
  [FormFieldId.BILLING_STREET_ADDRESS]: labels.streetAddress,
  [FormFieldId.BILLING_POSTAL_CODE]: labels.postalCode,
  [FormFieldId.BILLING_CITY]: labels.city,
  [FormFieldId.BILLING_COUNTRY]: labels.country,
  [FormFieldId.BILLING_EMAIL]: labels.email,
  [FormFieldId.BILLING_PHONE]: labels.telephone,
};

function buildB2CForms(order) {
  const isSeparateDelivery = order.getCustomerField(
    FormFieldId.SEPARATE_DELIVERY_ADDRESS
  ).field;

  const isBillingCompanyField = order.getCustomerField(
    FormFieldId.BILLING_IS_COMPANY
  ).field;

  const isDeliveryCompanyField = order.getCustomerField(
    FormFieldId.DELIVERY_IS_COMPANY
  ).field;

  const billingFormFields = {};

  if (isBillingCompanyField) {
    billingFormFields[FormFieldId.BILLING_IS_COMPANY] = createFormField(
      safeCloneDeep(isBillingCompanyField)
    );
  }

  if (isSeparateDelivery) {
    billingFormFields[FormFieldId.SEPARATE_DELIVERY_ADDRESS] = createFormField(
      safeCloneDeep(isSeparateDelivery)
    );
  }

  const deliveryFormFields = {};

  const deliveryAddressDropdownField = order.getCustomerField(
    FormFieldId.DELIVERY_ADDRESS_DROPDOWN
  ).field;
  const hasDeliveryAddressDropdown = !!deliveryAddressDropdownField;

  const oneTimeDeliveryField = order.getCustomerField(
    FormFieldId.ONE_TIME_DELIVERY_ADDRESS
  ).field;
  const isOneTimeDeliveryAddress = normalizeBooleanField(
    oneTimeDeliveryField?.value
  );

  const includeDeliveryLineFieldsOnForm =
    !hasDeliveryAddressDropdown || isOneTimeDeliveryAddress;

  if (
    isDeliveryCompanyField &&
    (!hasDeliveryAddressDropdown || isOneTimeDeliveryAddress)
  ) {
    deliveryFormFields[FormFieldId.DELIVERY_IS_COMPANY] = createFormField(
      safeCloneDeep(isDeliveryCompanyField),
      [],
      () => isSeparateDelivery?.value
    );
  }

  const getValidators = (formField, formLabels) => {
    const validators = [];

    if (formField.required) {
      validators.push(notBlank(formLabels[formField.id]));
    }

    if (formField.min_length) {
      validators.push(
        minLength(formLabels[formField.id], formField.min_length)
      );
    }

    if (formField.max_length) {
      validators.push(
        maxLength(formLabels[formField.id], formField.max_length)
      );
    }

    if (formField.type === FormFieldType.EMAIL) {
      validators.push(isEmail(labels.email));
    }

    return validators;
  };

  const setIsActive = (formField) => {
    switch (formField.id) {
      case FormFieldId.BILLING_COMPANY:
      case FormFieldId.BILLING_VAT_NUMBER:
        if (!isBillingCompanyField) return null;
        return () => isBillingCompanyField?.value || false;
      case FormFieldId.DELIVERY_COMPANY:
        if (!isDeliveryCompanyField) return null;
        return () => isDeliveryCompanyField?.value || false;
      default:
        return null;
    }
  };

  order.billingFields.forEach((formField) => {
    const validators = getValidators(formField, b2cBillingFormLabels);
    billingFormFields[formField.id] = createFormField(
      safeCloneDeep(formField),
      validators,
      setIsActive(formField)
    );
  });

  if (hasDeliveryAddressDropdown) {
    const validators = getValidators(
      {
        ...deliveryAddressDropdownField,
        id: FormFieldId.DELIVERY_ADDRESS_DROPDOWN,
      },
      b2cDeliveryFormLabels
    );
    deliveryFormFields[FormFieldId.DELIVERY_ADDRESS_DROPDOWN] = createFormField(
      safeCloneDeep({
        ...deliveryAddressDropdownField,
        id: FormFieldId.DELIVERY_ADDRESS_DROPDOWN,
      }),
      validators,
      null
    );
  }

  if (includeDeliveryLineFieldsOnForm) {
    order.deliveryFields.forEach((formField) => {
      if (formField.id === FormFieldId.DELIVERY_ADDRESS_DROPDOWN) {
        return;
      }
      if (deliveryFormFields[formField.id]) {
        return;
      }
      const validators = getValidators(formField, b2cDeliveryFormLabels);
      deliveryFormFields[formField.id] = createFormField(
        snapshotDeliveryCountrySelectForForm(formField),
        validators,
        setIsActive(formField)
      );
    });
  }

  return {
    billingForm: createForm(billingFormFields),
    deliveryForm: createForm(deliveryFormFields),
    deliveryDropdownForm: null,
  };
}

function buildB2BForms(order) {
  const deliveryAddressDropdownField = order.getCustomerField(
    FormFieldId.DELIVERY_ADDRESS_DROPDOWN
  ).field;

  const isBillingCompanyField = order.getCustomerField(
    FormFieldId.BILLING_IS_COMPANY
  ).field;

  const isSeparateDeliveryField = order.getCustomerField(
    FormFieldId.SEPARATE_DELIVERY_ADDRESS
  ).field;

  const billingFormFields = {};

  if (isBillingCompanyField) {
    const normalizedValue = normalizeBooleanField(isBillingCompanyField.value);

    billingFormFields[FormFieldId.BILLING_IS_COMPANY] = createFormField({
      ...safeCloneDeep(isBillingCompanyField),
      value: normalizedValue,
    });
  }

  if (isSeparateDeliveryField) {
    const normalizedValue = normalizeBooleanField(
      isSeparateDeliveryField.value
    );
    billingFormFields[FormFieldId.SEPARATE_DELIVERY_ADDRESS] = createFormField({
      ...safeCloneDeep(isSeparateDeliveryField),
      value: normalizedValue ? '1' : null,
    });
  }

  const billingCountryField = order.getCustomerField(
    FormFieldId.BILLING_COUNTRY
  ).field;
  if (
    billingCountryField &&
    billingCountryField.type === FormFieldType.SELECT
  ) {
    const initialValue =
      billingCountryField.value !== null &&
      billingCountryField.value !== undefined
        ? String(billingCountryField.value)
        : '';

    billingFormFields[FormFieldId.BILLING_COUNTRY] = createFormField(
      {
        ...safeCloneDeep(billingCountryField),
        value: initialValue,
      },
      billingCountryField.required ? [notBlank(labels.country)] : []
    );
  }

  order.billingFields.forEach((formField) => {
    const validators = getCheckoutFieldValidators(
      formField,
      b2bBillingFormLabels
    );
    billingFormFields[formField.id] = createFormField(
      safeCloneDeep(formField),
      validators
    );
  });

  const deliveryDropdownFormFields = {};
  if (
    deliveryAddressDropdownField &&
    deliveryAddressDropdownField.type === FormFieldType.SELECT
  ) {
    const rawValue = deliveryAddressDropdownField.value;
    // Keep form value in sync with the order even when options lag (e.g. newly saved
    // address id not yet present on deliveryAddressDropdownField.options). UI options
    // from useDeliveryAddressBookActions add a matching synthetic row in that case.
    const initialValue =
      rawValue !== null &&
      rawValue !== undefined &&
      String(rawValue).trim() !== ''
        ? String(rawValue)
        : '';

    deliveryDropdownFormFields[FormFieldId.DELIVERY_ADDRESS_DROPDOWN] =
      createFormField(
        {
          ...safeCloneDeep(deliveryAddressDropdownField),
          value: initialValue,
        },
        deliveryAddressDropdownField.required
          ? [notBlank(labels.deliveryAddressId)]
          : []
      );
  }

  const billingForm = createForm(billingFormFields);
  const deliveryDropdownForm = createForm(deliveryDropdownFormFields);

  let deliveryForm = null;
  if (order.deliveryFields.length > 0) {
    const isSeparateDelivery = order.getCustomerField(
      FormFieldId.SEPARATE_DELIVERY_ADDRESS
    ).field;
    const isDeliveryCompanyField = order.getCustomerField(
      FormFieldId.DELIVERY_IS_COMPANY
    ).field;

    const deliveryFormFieldsForForm = {};
    if (isDeliveryCompanyField) {
      deliveryFormFieldsForForm[FormFieldId.DELIVERY_IS_COMPANY] =
        createFormField(
          safeCloneDeep(isDeliveryCompanyField),
          [],
          () => isSeparateDelivery?.value
        );
    }
    order.deliveryFields.forEach((formField) => {
      const validators = getCheckoutFieldValidators(
        formField,
        b2bDeliveryFormLabels
      );
      const setIsActiveDelivery = (formFieldForActive) => {
        if (formFieldForActive.id === FormFieldId.DELIVERY_COMPANY) {
          if (!isDeliveryCompanyField) return null;
          return () => isDeliveryCompanyField?.value || false;
        }
        return null;
      };
      deliveryFormFieldsForForm[formField.id] = createFormField(
        snapshotDeliveryCountrySelectForForm(formField),
        validators,
        setIsActiveDelivery(formField)
      );
    });
    deliveryForm = createForm(deliveryFormFieldsForForm);
  }

  return {
    billingForm,
    deliveryDropdownForm,
    deliveryForm,
  };
}

function b2cFormsDependencyKey(order) {
  return [
    order.billingFields.length,
    order.deliveryFields.length,
    order.getCustomerField(FormFieldId.DELIVERY_ADDRESS_DROPDOWN).field,
    order.getCustomerField(FormFieldId.BILLING_CITY).value,
    order.getCustomerField(FormFieldId.BILLING_COMPANY).value,
    order.getCustomerField(FormFieldId.BILLING_COUNTRY).value,
    order.getCustomerField(FormFieldId.BILLING_EMAIL).value,
    order.getCustomerField(FormFieldId.BILLING_FIRST_NAME).value,
    order.getCustomerField(FormFieldId.BILLING_IS_COMPANY).value,
    order.getCustomerField(FormFieldId.BILLING_LAST_NAME).value,
    order.getCustomerField(FormFieldId.BILLING_PHONE).value,
    order.getCustomerField(FormFieldId.BILLING_POSTAL_CODE).value,
    order.getCustomerField(FormFieldId.BILLING_STREET_ADDRESS).value,
    order.getCustomerField(FormFieldId.BILLING_VAT_NUMBER).value,
    order.getCustomerField(FormFieldId.SEPARATE_DELIVERY_ADDRESS).value,
    order.getCustomerField(FormFieldId.ONE_TIME_DELIVERY_ADDRESS).field?.value,
    order.getCustomerField(FormFieldId.DELIVERY_ADDRESS_DROPDOWN).value,
    order.getCustomerField(FormFieldId.DELIVERY_CITY).value,
    order.getCustomerField(FormFieldId.DELIVERY_COMPANY).value,
    order.getCustomerField(FormFieldId.DELIVERY_COUNTRY).value,
    order.getCustomerField(FormFieldId.DELIVERY_FIRST_NAME).value,
    order.getCustomerField(FormFieldId.DELIVERY_IS_COMPANY).value,
    order.getCustomerField(FormFieldId.DELIVERY_LAST_NAME).value,
    order.getCustomerField(FormFieldId.DELIVERY_PHONE).value,
    order.getCustomerField(FormFieldId.DELIVERY_POSTAL_CODE).value,
    order.getCustomerField(FormFieldId.DELIVERY_STREET_ADDRESS).value,
  ];
}

function b2bFormsDependencyKey(order) {
  return [
    order.billingFields.length,
    order.deliveryFields.length,
    order.getCustomerField(FormFieldId.BILLING_IS_COMPANY).value,
    order.getCustomerField(FormFieldId.BILLING_COUNTRY).value,
    order.getCustomerField(FormFieldId.SEPARATE_DELIVERY_ADDRESS).field?.value,
    order.getCustomerField(FormFieldId.DELIVERY_ADDRESS_DROPDOWN).field?.value,
    order.getCustomerField(FormFieldId.DELIVERY_ADDRESS_DROPDOWN).field
      ?.options,
    order.billingFields.map((f) => f.id).join(','),
    order.deliveryFields.map((f) => f.id).join(','),
  ];
}

/**
 * Checkout billing/delivery MobX forms from order.customer_fields.
 * B2B vs B2C is determined by order.isB2BCheckout.
 */
export function useRecipientCheckoutForms(order) {
  const isB2B = order.isB2BCheckout;

  return useMemo(() => {
    if (isB2B) {
      return buildB2BForms(order);
    }
    return buildB2CForms(order);
  }, [
    isB2B,
    ...(isB2B ? b2bFormsDependencyKey(order) : b2cFormsDependencyKey(order)),
  ]);
}
